import { createClass as _createClass, classCallCheck as _classCallCheck } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { globalEventDispatcher } from './GlobalEventDispatcher.js';
import { noop } from './util/type.js';
import { getX, getY, isTouch } from './util/event.js';

var SwipeState;

(function (SwipeState) {
  SwipeState[SwipeState["idle"] = 0] = "idle";
  SwipeState[SwipeState["detecting"] = 1] = "detecting";
  SwipeState[SwipeState["swiping"] = 2] = "swiping";
})(SwipeState || (SwipeState = {}));
/* eslint max-classes-per-file: ["error", 2] */

/** All the callbacks we support. When instantiated, defaults all of them to noops, but keeps any it
finds on a provided object. */


var SwipeCallbacks = function SwipeCallbacks() {
  var _this = this;

  var obj = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  _classCallCheck(this, SwipeCallbacks);

  /** Called when a swipe starts */
  this.onSwipeStart = noop;
  /** Called when the pointer changes position during a swipe, or swipe detection */

  this.onSwipeMove = noop;
  /** Called when the threshold is met and we've recognized an intentional swipe */

  this.onSwipeConfirmed = noop;
  /** Called when a swipe ends */

  this.onSwipeEnd = noop;
  Object.getOwnPropertyNames(this).filter(function (callbackName) {
    return obj[callbackName];
  }).forEach(function (callbackName) {
    return _this[callbackName] = obj[callbackName];
  });
};

var LogLevel;

(function (LogLevel) {
  LogLevel[LogLevel["none"] = 0] = "none";
  LogLevel[LogLevel["debug"] = 1] = "debug";
})(LogLevel || (LogLevel = {}));

var logLevel = LogLevel.none;
var HorizontalSwipeTracker = /*#__PURE__*/function () {
  function HorizontalSwipeTracker(el, config) {
    var _this2 = this;

    _classCallCheck(this, HorizontalSwipeTracker);

    /** Internal state machine state */
    this._state = SwipeState.idle;
    /** the horizontal distance in px that must be travelled before we consider a gesture a swipe */

    this.swipeThreshold = 10;
    /** the vertical distance in px that must be travelled before we eliminate a gesture from
    consideration as a horizontal swipe */

    this.escapeThreshold = 10;

    this.startDetectingSwipe = function (event) {
      _this2._state = SwipeState.detecting; // We set the pointerup/touchend/mouseup event to "once" just to be safe. All events will get
      // reset next time through. We must be careful to call removeEventListener when the swipe is
      // finished, OR when the pointer is released before the threshold is met.

      if (isTouch(event)) {
        window.addEventListener('touchend', _this2.endSwipe, {
          once: true
        });
        globalEventDispatcher('touchmove').add(_this2.onPointerMove);
      } else {
        window.addEventListener('mouseup', _this2.endSwipe, {
          once: true
        });
        window.addEventListener('mousemove', _this2.onPointerMove, {
          passive: false
        });
      }

      _this2.startX = getX(event);
      _this2.startY = getY(event);
      _this2.currentX = _this2.startX;
      _this2.currentY = _this2.startY;

      _this2.debugLog();
    };

    this.onPointerMove = function (event) {
      if (_this2.state === SwipeState.idle) return;

      _this2.debugLog();

      _this2.currentX = getX(event);
      _this2.currentY = getY(event);

      switch (_this2.state) {
        case SwipeState.detecting:
          if (_this2.escapeThresholdIsMet(event)) _this2.endSwipe();

          if (_this2.thresholdIsMet(event)) {
            _this2._state = SwipeState.swiping;

            _this2.callbacks.onSwipeMove(_this2.distanceX, true);

            _this2.callbacks.onSwipeConfirmed(_this2.distanceX, true);
          } else {
            _this2.callbacks.onSwipeMove(_this2.distanceX, false);

            _this2.callbacks.onSwipeStart(_this2.distanceX, false);
          }

          break;

        case SwipeState.swiping:
          event.preventDefault();

          _this2.callbacks.onSwipeMove(_this2.distanceX, false);

          break;
      } // Record this event so it can be examined on the next call.


      _this2.lastMovement = event;
    };

    this.endSwipe = function () {
      var distanceX = _this2.distanceX,
          state = _this2.state; // Clean up event handlers

      window.removeEventListener('mousemove', _this2.onPointerMove);
      globalEventDispatcher('touchmove').remove(_this2.onPointerMove);

      _this2.callbacks.onSwipeEnd(distanceX, state === SwipeState.swiping); // Reset all internal state


      _this2._state = SwipeState.idle;
      _this2.startX = undefined;
      _this2.startY = undefined;
      _this2.currentX = undefined;
      _this2.currentY = undefined;
      _this2.lastMovement = undefined;

      _this2.debugLog();
    };

    this.el = el;
    this.el.addEventListener('mousedown', this.startDetectingSwipe);
    this.el.addEventListener('touchstart', this.startDetectingSwipe);
    this.el.addEventListener('dragstart', this.cancelNativeDrag); // BROWSER WORKAROUND: Safari
    // Due to Safari bug 184250 (https://bugs.webkit.org/show_bug.cgi?id=184250), we can't use
    // .preventDefault() on events that are dynamically bound. This is a bummer, because we really
    // need to be able to selectively preventDefault() touchmove events to prevent touchscreen
    // browsers from vertically scrolling a little (or a lot) during a horizontal swipe.
    //
    // To avoid the negative effect of registering one event handler per filmstrip on the page,
    // we have this nifty dispatcher here that will allow all SwipeControllers to share just the
    // one permanent 'touchmove' listener, but allow us to dynamically add and remove secondary
    // event listeners with the same semantics as .addEventListener() thereby working around
    // Safari's bug.

    globalEventDispatcher('touchmove'); // If config is null, then assume we've been passed a custom element with one or more
    // SwipeResponder callbacks on it!

    this.callbacks = new SwipeCallbacks(config || el);
  }
  /** Remove the only event listened set in the constructor */


  _createClass(HorizontalSwipeTracker, [{
    key: "destroy",
    value: function destroy() {
      this.el.removeEventListener('mousedown', this.startDetectingSwipe);
      this.el.removeEventListener('touchstart', this.startDetectingSwipe);
      this.el.removeEventListener('dragstart', this.cancelNativeDrag);
    }
    /** Output typically needed debug info */

  }, {
    key: "debugLog",
    value: function debugLog() {
      if (logLevel === LogLevel.debug) {
        /* eslint-disable no-console */
        console.log("state: ".concat(this.state, ", startX: ").concat(this.startX, ", currentX: ").concat(this.currentX, ", startY: ").concat(this.startY, ", currentY: ").concat(this.currentY, ", distanceX: ").concat(this.distanceX, ", distanceY: ").concat(this.distanceY));
      }
    } // ACCESSORS

    /** Calculate the current velocity of the pointer, in pixels per ms */

  }, {
    key: "getPointerVelocity",
    value: function getPointerVelocity(event) {
      if (this.lastMovement) {
        // the timestamps are in ms
        var timeDelta = event.timeStamp - this.lastMovement.timeStamp; // event.movementX contains the horizontal distance traveled since the last pointer event

        return event.movementX / timeDelta;
      }

      return 0;
    }
    /** Read-only accessor for state. We don't want consumers messing with the state, but they should
    be able to see it. */

  }, {
    key: "thresholdIsMet",

    /** Test if user has moved the pointer enough to consider this an intentional swipe gesture */
    value: function thresholdIsMet(event) {
      if (this.startX !== undefined) {
        var distanceFromStart = Math.abs(getX(event) - this.startX);
        return distanceFromStart > this.swipeThreshold;
      }

      return false;
    }
    /** Test if the user has moved the pointer vertically enough that we should bail and let the page
    scroll on its own */

  }, {
    key: "escapeThresholdIsMet",
    value: function escapeThresholdIsMet(event) {
      if (this.startY !== undefined) {
        var y = getY(event);
        var distanceFromStart = Math.abs(y - this.startY);
        var met = distanceFromStart > this.escapeThreshold;

        if (logLevel === LogLevel.debug) {
          console.log('escape threshold is', met ? 'met!' : 'not met.');
        }

        return met;
      }

      return false;
    } // ACTIONS

  }, {
    key: "cancelNativeDrag",
    value: function cancelNativeDrag(e) {
      e.preventDefault();
    }
  }, {
    key: "state",
    get: function get() {
      return this._state;
    }
    /** Calculate the horizontal distance traveled by the swipe. Negative values mean to the left of
    the point of origin. */

  }, {
    key: "distanceX",
    get: function get() {
      if (this.startX !== undefined && this.currentX !== undefined) {
        return this.currentX - this.startX;
      }

      return 0;
    }
  }, {
    key: "distanceY",
    get: function get() {
      if (this.startY !== undefined && this.currentY !== undefined) {
        return this.currentY - this.startY;
      }

      return 0;
    }
  }]);

  return HorizontalSwipeTracker;
}();

export { HorizontalSwipeTracker, SwipeState };
